<template>
  <div>
    <el-row :gutter="20">
      <el-col :span="12">
        <FormItem :label="$t('advancedRevision.wizard.analysis')">
          <el-select
            v-model="newTrail.labSampleAnalysisId"
            filterable
            placeholder=""
            style="width: 100%"
          >
            <el-option
              v-for="opt in analysisOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </FormItem>
      </el-col>
      <el-col :span="12">
        <FormItem :label="$t('advancedRevision.wizard.thermostatName')">
          <el-autocomplete
            v-model="newTrail.thermostatName"
            style="width: 100%"
            :fetch-suggestions="thermostatList"
            value-key="label"
            placeholder=""
            @input="newTrail.thermostatId = null"
            @select="
              (item) => {
                newTrail.thermostatId = item.value;
              }
            "
          />
        </FormItem>
      </el-col>
    </el-row>
    <el-row
      :gutter="20"
      type="flex"
      align="bottom"
    >
      <el-col :span="8">
        <FormItem :label="$t('advancedRevision.wizard.startedAt')">
          <el-date-picker
            v-model="newTrail.startedAt"
            type="datetime"
            format="dd-MM-yyyy HH:mm"
            style="width: 100%"
          />
        </FormItem>
      </el-col>
      <el-col :span="8">
        <FormItem :label="$t('advancedRevision.wizard.endedAt')">
          <el-date-picker
            v-model="newTrail.endedAt"
            type="datetime"
            format="dd-MM-yyyy HH:mm"
            style="width: 100%"
          />
        </FormItem>
      </el-col>
      <el-col :span="8">
        <el-button
          type="primary"
          class="add-trail-btn"
          :disabled="!canAddTrail"
          @click.prevent="addTrail"
        >
          {{ $t('advancedRevision.wizard.addTrail') }}
        </el-button>
      </el-col>
    </el-row>

    <el-divider />

    <el-collapse
      v-model="accordionActiveItem"
      accordion
    >
      <el-collapse-item
        v-for="(trail, index) in trails"
        :key="trail.temperatureTrailId || index"
        :name="index"
      >
        <template slot="title">
          <div class="trail-title">
            <span>
              {{ trail.thermostatName }} - {{ trail.analysisDescription }}
            </span>
            <span
              class="trail-delete"
              :title="$t('advancedRevision.wizard.deleteTrail')"
              @click.stop="handleDelete(trail)"
            >
              <i class="state-icon fas fa-trash" />
            </span>
          </div>
        </template>
        <el-row :gutter="20">
          <el-col :span="6">
            <FormItem :label="$t('advancedRevision.wizard.temperature')">
              <el-input-number
                v-model="trail.temperature"
                :controls="false"
                style="width: 100%"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('advancedRevision.wizard.startedAt')">
              <el-date-picker
                v-model="trail.startedAt"
                type="datetime"
                format="dd-MM-yyyy HH:mm"
                style="width: 100%"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('advancedRevision.wizard.endedAt')">
              <el-date-picker
                v-model="trail.endedAt"
                type="datetime"
                format="dd-MM-yyyy HH:mm"
                style="width: 100%"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('advancedRevision.wizard.thermostatName')">
              <el-autocomplete
                v-model="trail.thermostatName"
                style="width: 100%"
                :fetch-suggestions="thermostatList"
                value-key="label"
                placeholder=""
                @input="trail.thermostatId = null"
                @select="
                  (item) => {
                    trail.thermostatId = item.value;
                  }
                "
              />
            </FormItem>
          </el-col>
        </el-row>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
  import { thermostatsApi, advancedRevisionApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';

  const emptyTrail = () => ({
    startedAt: null,
    endedAt: null,
    thermostatName: null,
    thermostatId: null,
    labSampleAnalysisId: null,
  });

  export default {
    name: 'RevisionTemperatureStep',
    components: { FormItem },
    props: {
      trails: { type: Array, required: true },
      analysisOptions: { type: Array, default: () => [] },
    },
    data() {
      return {
        accordionActiveItem: 0,
        newTrail: emptyTrail(),
      };
    },
    computed: {
      canAddTrail() {
        return (
          this.newTrail.thermostatId != null &&
          this.newTrail.labSampleAnalysisId != null &&
          this.newTrail.startedAt != null
        );
      },
    },
    methods: {
      async thermostatList(value, cb) {
        const response = await thermostatsApi.autocompleteFetch(value);
        cb((response.data || []).map((t) => ({ label: t.name, value: t.id })));
      },
      async addTrail() {
        if (!this.canAddTrail) return;
        this.$emit('update:loading', true);
        try {
          await advancedRevisionApi.addTemperatureTrail({
            startedAt: this.newTrail.startedAt,
            endedAt: this.newTrail.endedAt,
            thermostatId: this.newTrail.thermostatId,
            labSampleAnalysisId: this.newTrail.labSampleAnalysisId,
          });
          this.newTrail = emptyTrail();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('advancedRevision.wizard.trailAdded'),
          });
          // Keep the overlay up: the parent's refetch turns it off once the
          // list is reloaded, so it stays on from click through refresh.
          this.$emit('refresh');
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error?.response?.data?.error?.message ??
              this.$t('advancedRevision.wizard.trailError'),
          });
          // No refetch on error: clear the overlay here.
          this.$emit('update:loading', false);
        }
      },
      async handleDelete(trail) {
        try {
          await this.$confirm(
            this.$t('advancedRevision.wizard.deleteTrailConfirm'),
            this.$t('advancedRevision.wizard.deleteTrailConfirmTitle'),
            {
              confirmButtonText: this.$t('global.confirm'),
              cancelButtonText: this.$t('global.cancel'),
            }
          );
        } catch {
          return;
        }

        // Overlay stays on for the whole operation (delete + reconcile
        // refetch); the list only changes when the refresh completes, so the
        // UX is: confirm -> overlay -> updated list.
        this.$emit('update:loading', true);
        try {
          await advancedRevisionApi.deleteTemperatureTrail(
            trail.temperatureTrailId
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('advancedRevision.wizard.trailDeleted'),
          });
          // Parent's refetch turns the overlay off after reloading the list.
          this.$emit('refresh');
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error?.response?.data?.error?.message ??
              this.$t('advancedRevision.wizard.trailError'),
          });
          this.$emit('update:loading', false);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .trail-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .trail-delete {
    cursor: pointer;
    color: $--color-danger;
    padding: 0 8px;
  }
  // Match FormItem's bottom margin so the button, aligned to the row bottom,
  // lines up with the input fields rather than sitting below them.
  .add-trail-btn {
    margin-bottom: 2.2rem;
  }
</style>
